import React, { Component } from "react";
import PropTypes from "prop-types";
import { calcAge, capitalize, formatDate, formatDateTime, formatDateTimeUTC, getViewPatientData } from "../../../lib";
import { Sparklines, SparklinesLine, SparklinesSpots } from 'react-sparklines';

import kidneyIcon from "../../../assets/disease-icons/kidney.png";
import obesityIcon from "../../../assets/disease-icons/obesity.png";
import neurologyIcon from "../../../assets/disease-icons/neurology.png";
import liverIcon from "../../../assets/disease-icons/liver.png";
import lungsIcon from "../../../assets/disease-icons/lungs.png";
import heartIcon from "../../../assets/disease-icons/heart.png";
import sickleCellIcon from "../../../assets/disease-icons/sickleCell.png";
import strokeIcon from "../../../assets/disease-icons/stroke.png";
import dementiaIcon from "../../../assets/disease-icons/dementia.png";
import immunocompromisedIcon from "../../../assets/disease-icons/immunocompromised.png";

/* ─── Constants ─── */
const PAGE_SIZE = 50;

const STATUS_CONFIG = {
    Critical: { color: "#d9534f", label: "label-danger", bg: "#fff5f5", border: "#f5c6cb" },
    High: { color: "#e67e22", label: "label-warning", bg: "#fffbf0", border: "#fde8c0" },
    Elevated: { color: "#2980b9", label: "label-info", bg: "#f0f8ff", border: "#bee3f8" },
};

const TREND_CFG = {
    WORSENING: { icon: "↑", label: "Worsening", color: "#d9534f" },
    IMPROVING: { icon: "↓", label: "Improving", color: "#ffc107" },
    STABLE: { icon: "→", label: "Stable", color: "#27ae60" },
};

/* ─── Markdown-lite renderer for ai_recommendation ─── */
const AiRec = ({ text }) => {
    if (!text) return null;
    const lines = text.split("\n");
    const out = [];
    let key = 0;
    lines.forEach((line) => {
        if (!line.trim()) { out.push(<br key={key++} />); return; }
        const boldHeader = line.match(/^\*\*(.*)\*\*$/);
        if (boldHeader) {
            out.push(
                <div key={key++} style={{ fontWeight: 700, color: "#1a2340", marginTop: 10, marginBottom: 2, fontSize: 12 }}>
                    {boldHeader[1]}
                </div>
            );
            return;
        }
        const bullet = line.match(/^[\*\-]\s+(.+)/) || line.match(/^\d+\.\s+(.+)/);
        if (bullet) {
            out.push(
                <div key={key++} style={{ display: "flex", gap: 6, marginBottom: 3 }}>
                    <span style={{ color: "#2980b9", flexShrink: 0, marginTop: 1 }}>•</span>
                    <span
                        style={{ fontSize: 12, color: "#3a4560", lineHeight: 1.5 }}
                        dangerouslySetInnerHTML={{ __html: bullet[1].replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>") }}
                    />
                </div>
            );
            return;
        }
        out.push(
            <div
                key={key++}
                style={{ fontSize: 12, color: "#3a4560", lineHeight: 1.6, marginBottom: 2 }}
                dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>") }}
            />
        );
    });
    return <div>{out}</div>;
};

/* ─── PatientCard ─── */
class PatientCard extends Component {
    state = { expanded: false, showAiRec: false };

    toggle = () => this.setState((s) => ({ expanded: !s.expanded }));
    toggleRec = (e) => { e.stopPropagation(); this.setState((s) => ({ showAiRec: !s.showAiRec })); };


    render() {
        const getTrendIcon = (trend) => {
            switch (trend) {
                case "up":
                    return "↑";
                case "down":
                    return "↓";
                default:
                    return "→";
            }
        };
        const { p } = this.props;
        const { expanded, showAiRec } = this.state;

        const mrn = p.mrn || "—";
        const fullName = `${capitalize(p.first_name || "")} ${capitalize(p.last_name || "")}`.trim();
        const age = calcAge(p.date_of_birth);
        const gender = p.gender || "—";
        const obsDate = formatDateTimeUTC(p.observation_date);
        const lastAttempt = formatDateTimeUTC(p.last_attempt);
        const a1c = parseFloat(p.latest_a1c_value) || 0;
        const status = capitalize(p.severity_tier || "Elevated");
        const trendKey = (p.trend || "STABLE").toUpperCase();
        const trend = TREND_CFG[trendKey] || TREND_CFG.STABLE;
        const phones = (p.phone_numbers || "").split(",").map(s => s.trim()).filter(Boolean);
        const phoneTypes = (p.phone_types || "").split(",").map(s => s.trim());
        const email = p.email_address || null;
        const language = p.primary_language || "—";
        const hedisMet = p.hedis_measure_met === "YES";
        const aiRec = p.ai_recommendation || null;
        const leadStatus = p.lead_status || "—";
        const memberStatus = p.member_status || "—";

        const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.Elevated;
        const a1cBarWidth = Math.min((a1c / 15) * 100, 100);

        const reading = p.a1c_history.map(r => parseFloat(r.a1c_value) || 0);
        const sparkData = reading.length > 0 ? reading : [a1c];

        const getTrend = (values) => {
            if (values.length < 2) return "flat";

            const last = values[values.length - 1];
            const prev = values[values.length - 2];

            if (last > prev) return "up";
            if (last < prev) return "down";
            return "flat";
        };

        const trendIcon = getTrend(sparkData);

        const arrow = getTrendIcon(trendIcon);

        return (
            <div className="patient-card" style={{
                background: "#fff", borderRadius: 10, marginBottom: 12,
                boxShadow: expanded ? "0 6px 24px rgba(0,0,0,0.10)" : "0 2px 8px rgba(0,0,0,0.06)",
                border: `1.5px solid ${expanded ? cfg.color : "#e8edf2"}`,
                overflow: "hidden", transition: "box-shadow 0.2s, border-color 0.2s",
            }}>

                {/* ── Collapsed Header Row ── */}
                <div onClick={this.toggle} style={{
                    display: "flex", alignItems: "center", flexWrap: "nowrap", justifyContent: "space-between",
                    padding: "14px 20px", cursor: "pointer",
                    background: expanded ? cfg.bg : "#fff", transition: "background 0.2s",
                }}>

                    <div style={{ flex: "0 0 210px", minWidth: 0, display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{ width: 4, height: 52, borderRadius: 4, background: cfg.color, marginRight: 5, flexShrink: 0 }} />
                        <div>
                            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 12, fontWeight: 700, color: "#1a2340", letterSpacing: 0.4 }}>{mrn}</div>
                            <div style={{ fontSize: 12, color: "#7a8599", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {fullName} · {age}y {gender.charAt(0)}
                            </div>
                            {p?.PCP_Name && (
                                <div>
                                    <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 12, fontWeight: 700, color: "#1a2340", letterSpacing: 0.4 }}>PCP:</span>
                                    <span style={{ fontSize: 11, color: "#7a8599", marginTop: 4, fontWeight: 500 }}>{p.PCP_Name}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={{ flex: "0 0 200px" }}>
                        <div style={{ fontSize: 10, color: "#aab3c0", textTransform: "uppercase", letterSpacing: 0.8 }}>Last Obs</div>
                        <div style={{ fontSize: 12, color: "#3a4560", marginTop: 2, fontWeight: 500 }}>{obsDate}</div>
                    </div>

                    <div style={{ flex: "0 0 100px" }} className="text-center">
                        <div style={{ fontSize: 22, fontWeight: 700, color: cfg.color, fontFamily: "'Space Mono', monospace", lineHeight: 1 }}>{a1c}%</div>
                        <div style={{ fontSize: 10, color: "#aab3c0", textTransform: "uppercase", letterSpacing: 0.8 }}>A1c</div>
                    </div>

                    <div style={{ flex: "0 0 210px", marginRight: 20, minWidth: 60 }}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                            <div style={{ flex: "0 0 210px", marginRight: 10 }}>
                                <Sparklines data={sparkData} width={100} height={6}>
                                    <SparklinesLine color={trend.color} style={{ strokeWidth: 0.7 }} />
                                    <SparklinesSpots size={2} />
                                </Sparklines>
                            </div>

                            <div style={{ fontSize: 14 }} className="text-danger">
                                {arrow}
                            </div>
                        </div>
                        <div style={{ fontSize: 10, color: "#bbb", marginTop: 3 }}>
                            {reading.length} readings
                        </div>
                    </div>

                    <div style={{ flex: "0 0 86px", textAlign: "center" }}>
                        <span className={`label ${cfg.label}`} style={{ fontSize: 11, padding: "4px 10px", borderRadius: 20 }}>{status}</span>
                    </div>

                    <div style={{ flex: "0 0 140px", textAlign: "center" }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "#3a4560" }}>
                            {leadStatus === "LEAD" ? (
                                <span className="text-muted">Lead</span>
                            ) : (
                                memberStatus === "ACTIVE" && (
                                    <span className="text-success">Active Member</span>
                                )
                            )}
                        </div>
                    </div>

                    <div style={{ flex: "0 0 100px", textAlign: "center" }}>
                        <div style={{ marginBottom: 5 }}>
                            <span style={{ color: trend.color, fontWeight: 700, fontSize: 16 }}>{trend.icon}</span>
                            <span style={{ fontSize: 12, color: trend.color, marginLeft: 4 }}>{trend.label}</span>
                        </div>
                        <div className="diseaseIcons" style={{ display: "flex", gap: "5px", justifyContent: "center" }}>
                            {p.Heart == 1 && <span title="Heart Disease"><img width="25px" src={heartIcon} /></span>}
                            {p.Lung == 1 && <span title="Lung Disease"><img width="25px" src={lungsIcon} /></span>}
                            {p.Liver_Disease == 1 && <span title="Liver Disease"><img width="25px" src={liverIcon} /></span>}
                            {p.Mental_Health == 1 && <span title="Mental Health"><img width="25px" src={neurologyIcon} /></span>}
                            {p.Overweight_and_Obesity == 1 && <span title="Overweight and Obesity"><img width="25px" src={obesityIcon} /></span>}
                            {p.Chronic_Kidney_Disease == 1 && <span title="Chronic Kidney Disease"><img width="25px" src={kidneyIcon} /></span>}
                            {p.Sickle_Cell_Disease_or_Thalassemia == 1 && <span><img width="25px" src={sickleCellIcon} /></span>}
                            {p.Stroke_or_Cerebrovascular_Disease == 1 && <span><img width="25px" src={strokeIcon} /></span>}
                            {p.Dementia == 1 && <span><img width="25px" src={dementiaIcon} /></span>}
                            {p.Immunocompromised == 1 && <span><img width="25px" src={immunocompromisedIcon} /></span>}
                            {/* {p.Down_Syndrome == 1 && <span><img width="25px" src={downSyndromeIcon} /></span>} */}
                        </div>
                    </div>

                    <div style={{ flex: "0 0 26px", textAlign: "right" }}>
                        <span style={{
                            display: "inline-block", color: "#aab3c0", fontSize: 18,
                            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                            transition: "transform 0.25s", lineHeight: 1,
                        }}>▾</span>
                    </div>
                </div>

                {/* ── Expanded Panel ── */}
                {expanded && (
                    <div style={{ borderTop: `1px solid ${cfg.border}`, background: cfg.bg, padding: "20px 24px", animation: "fadeSlideIn 0.2s ease" }}>
                        <div className="row">

                            <div className="col-md-6 col-sm-6" style={{ marginBottom: 16 }}>
                                <div className="detail-label" style={{ marginBottom: 6 }}>READING HISTORY</div>
                                <table className="table table-sm table-condensed">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>LOINC</th>
                                            <th>A1c %</th>
                                            <th>Met</th>
                                        </tr>
                                    </thead>
                                    <tbody style={{ fontSize: '12px' }}>
                                        {(p.a1c_history || []).map((r, i) => (
                                            <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#f9f9f9" }}>
                                                <td>{formatDateTimeUTC(r?.observation_date)}</td>
                                                <td>{r?.a1c_code || "—"}</td>
                                                <td style={{ fontFamily: "'Space Mono', monospace", color: "#1a2340", fontWeight: 500 }}>{parseFloat(r?.a1c_value).toFixed(1)}%</td>
                                                <td>
                                                    {r?.a1c_value < 9 ? (
                                                        <span style={{ color: "#27ae60", fontWeight: 700 }}>
                                                            <i className="fa fa-check"></i>
                                                        </span>
                                                    ) : (
                                                        <span style={{ color: "#d9534f", fontWeight: 700 }}>
                                                            <i className="fa fa-times"></i>
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="col-md-6 col-sm-6" style={{ marginBottom: 16 }}>
                                <div className="detail-label" style={{ marginBottom: 6 }}>AI CARE RECOMMENDATION</div>
                                <div style={{
                                    marginTop: 4, background: "#fff",
                                    border: `1px solid ${cfg.border}`,
                                    borderLeft: "3px solid #1e3c72",
                                    borderRadius: 8, padding: "14px 16px",
                                    animation: "fadeSlideIn 0.2s ease",
                                }}>
                                    <AiRec text={aiRec} />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

/* ─── Pagination Component ─── */
const Pagination = ({ currentPage, totalPages, totalRecords, pageSize, onPageChange, loading }) => {
    if (totalPages <= 1) return null;

    const startRecord = (currentPage - 1) * pageSize + 1;
    const endRecord = Math.min(currentPage * pageSize, totalRecords);

    /* Build page numbers: always show first, last, current ±1, with ellipsis gaps */
    const buildPages = () => {
        const pages = new Set([1, totalPages]);
        for (let i = Math.max(1, currentPage - 1); i <= Math.min(totalPages, currentPage + 1); i++) pages.add(i);
        const sorted = [...pages].sort((a, b) => a - b);
        const result = [];
        sorted.forEach((p, i) => {
            if (i > 0 && p - sorted[i - 1] > 1) result.push("...");
            result.push(p);
        });
        return result;
    };

    const pages = buildPages();

    const btnBase = {
        border: "1px solid #dce2ea",
        background: "#fff",
        color: "#3a4560",
        borderRadius: 6,
        padding: "5px 11px",
        fontSize: 12,
        fontWeight: 600,
        cursor: "pointer",
        transition: "all 0.15s",
        lineHeight: 1.4,
        fontFamily: "'DM Sans', sans-serif",
    };

    const btnActive = {
        ...btnBase,
        background: "#1e3c72",
        color: "#fff",
        border: "1px solid #1e3c72",
    };

    const btnDisabled = {
        ...btnBase,
        color: "#c5cdd8",
        cursor: "not-allowed",
        background: "#f7f9fb",
    };

    return (
        <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            flexWrap: "wrap", gap: 12,
            background: "#fff", borderRadius: 10, padding: "12px 18px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)", marginTop: 16,
        }}>
            {/* Record range info */}
            <div style={{ fontSize: 12, color: "#7a8599" }}>
                Showing <strong style={{ color: "#1a2340" }}>{startRecord}–{endRecord}</strong> of{" "}
                <strong style={{ color: "#1a2340" }}>{totalRecords}</strong> patients
            </div>

            {/* Page buttons */}
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                {/* Prev */}
                <button
                    style={currentPage === 1 || loading ? btnDisabled : btnBase}
                    disabled={currentPage === 1 || loading}
                    onClick={() => onPageChange(currentPage - 1)}
                    title="Previous page"
                >
                    ‹ Prev
                </button>

                {pages.map((p, i) =>
                    p === "..." ? (
                        <span key={`ellipsis-${i}`} style={{ padding: "5px 4px", color: "#aab3c0", fontSize: 13 }}>…</span>
                    ) : (
                        <button
                            key={p}
                            style={p === currentPage ? btnActive : (loading ? btnDisabled : btnBase)}
                            disabled={loading}
                            onClick={() => p !== currentPage && onPageChange(p)}
                        >
                            {p}
                        </button>
                    )
                )}

                {/* Next */}
                <button
                    style={currentPage === totalPages || loading ? btnDisabled : btnBase}
                    disabled={currentPage === totalPages || loading}
                    onClick={() => onPageChange(currentPage + 1)}
                    title="Next page"
                >
                    Next ›
                </button>
            </div>

            {/* Page jump */}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 12, color: "#7a8599" }}>Page</span>
                <input
                    type="number"
                    min={1}
                    max={totalPages}
                    defaultValue={currentPage}
                    key={currentPage}                        /* re-mount on page change to reset value */
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            const val = parseInt(e.target.value, 10);
                            if (val >= 1 && val <= totalPages && val !== currentPage) onPageChange(val);
                        }
                    }}
                    style={{
                        width: 52, padding: "4px 8px", borderRadius: 6,
                        border: "1px solid #dce2ea", fontSize: 12,
                        textAlign: "center", color: "#1a2340", fontFamily: "'DM Sans', sans-serif",
                    }}
                />
                <span style={{ fontSize: 12, color: "#7a8599" }}>of {totalPages}</span>
            </div>
        </div>
    );
};

/* ─── Main Dashboard ─── */
class A1cDashboard extends Component {
    static propTypes = {
        query: PropTypes.object,
        selection: PropTypes.object,
        location: PropTypes.object,
        settings: PropTypes.object,
        urlParams: PropTypes.object,
        dispatch: PropTypes.func,
    };

    constructor(props) {
        super(props);
        this.state = {
            filter: "all",
            loading: false,
            pageLoading: false,   // lighter spinner for page changes (summary stays visible)
            error: null,
            summary: null,
            patients: [],
            totalRecords: 0,
            currentPage: 1,
            totalPages: 1,
            memberStatus: "Active",   // New state for member status filter
            careGapType: "",   // New state for care gap type filter
            sortBy: "",
            sortDir: ""
        };
    }

    componentDidMount() {
        this.fetchAll();
    }

    /* Full load — fetches both summary and page 1 */
    fetchAll = async () => {
        try {
            this.setState({ loading: true, error: null });
            const baseUrl = this.props?.settings?.server?.baseUrl;

            const [summaryRes, dashboardRes] = await Promise.all([
                getViewPatientData({ url: `${baseUrl}/fetch/a1csummary`, server: this.props.settings.server }),
                getViewPatientData({ url: `${baseUrl}/fetch/a1cdashboard?page=1&size=${PAGE_SIZE}&status=${this.state.memberStatus}`, server: this.props.settings.server }),
            ]);

            const total = dashboardRes?.size || 0;   // API must return total record count
            const totalPages = Math.ceil(total / PAGE_SIZE) || 1;

            this.setState({
                summary: summaryRes,
                patients: dashboardRes?.records || [],
                totalRecords: total,
                totalPages,
                currentPage: dashboardRes?.page || 1,
                loading: false,
            });
        } catch (err) {
            console.error("Dashboard fetch failed:", err);
            this.setState({ error: err.message, loading: false });
        }
    };

    /* Page-only load — keeps summary intact, shows inline spinner */
    fetchPage = async (page) => {
        try {
            this.setState({ pageLoading: true, error: null, currentPage: page });
            const baseUrl = this.props?.settings?.server?.baseUrl;

            let url = `${baseUrl}/fetch/a1cdashboard?page=${page}&size=${PAGE_SIZE}`;
            if (this.state.careGapType != "") {
                url += `&careGapType=${this.state.careGapType}`;
            }
            if (this.state.memberStatus != "") {
                url += `&status=${this.state.memberStatus}`;
            }
            if (this.state.sortBy != "") {
                url += `&sortBy=${this.state.sortBy}`;
            }
            if (this.state.sortDir != "") {
                url += `&sortDir=${this.state.sortDir}`;
            }
            if (page !== "all" && this.state.filter !== "all") {
                url += `&severity=${capitalize(this.state.filter)}`;
            }
            const dashboardRes = await getViewPatientData({
                url: `${url}`,
                // url: `${baseUrl}/fetch/a1cdashboard?page=${page}&size=${PAGE_SIZE}${page !== "all" && this.state.filter !== "all" ? `&severity=${capitalize(this.state.filter)}` : ""}${this.state.memberStatus ? `&status=${this.state.memberStatus}` : ""}`,
                server: this.props.settings.server,
            });

            const total = dashboardRes?.size || this.state.totalRecords;
            const totalPages = Math.ceil(total / PAGE_SIZE) || 1;

            this.setState({
                patients: dashboardRes?.records || [],
                totalRecords: total,
                totalPages,
                currentPage: dashboardRes?.page || page,
                pageLoading: false,
            });
        } catch (err) {
            console.error("Page fetch failed:", err);
            this.setState({ error: err.message, pageLoading: false });
        }
    };

    setFilter = (filter) => {
        this.setState({ filter, currentPage: 1 }, () => {
            this.fetchPage(1);
        });
    };

    getFilteredPatients() {
        const { patients, filter } = this.state;
        const v = (p) => parseFloat(p.latest_a1c_value) || 0;
        if (filter === "critical") return patients.filter((p) => v(p) >= 10);
        if (filter === "high") return patients.filter((p) => v(p) >= 9 && v(p) < 10);
        if (filter === "elevated") return patients.filter((p) => v(p) >= 8 && v(p) < 9);
        return patients;
    }

    render() {
        const { filter, loading, pageLoading, error, summary, currentPage, totalPages, totalRecords } = this.state;
        const filtered = this.getFilteredPatients();

        const total = summary?.total_patients ?? "—";
        const active = summary?.active_members ?? "—";
        const inactive = summary?.inactive_members ?? "—";
        const lead = summary?.lead_members ?? "—";
        const critCount = summary?.critical_count ?? "—";
        const highCount = summary?.high_count ?? "—";
        const avgA1c = summary ? parseFloat(summary.avg_a1c).toFixed(1) + "%" : "—";
        const pctFlagged = summary ? parseFloat(summary.percent_flagged).toFixed(1) + "%" : "—";
        const flagged = (typeof critCount === "number" ? critCount : 0) + (typeof highCount === "number" ? highCount : 0);

        const statCards = [
            { label: "Total Members", value: typeof total === "number" ? total.toLocaleString() : total, color: "#1a2340", icon: "👥" },
            { label: "Active Members", value: typeof active === "number" ? active.toLocaleString() : active, color: "#27ae60", icon: "✅" },
            { label: "Inactive Members", value: typeof inactive === "number" ? inactive.toLocaleString() : inactive, color: "#d9534f", icon: "⛔" },
            { label: "Lead Members", value: typeof lead === "number" ? lead.toLocaleString() : lead, color: "#2980b9", icon: "🎯" },
            // { label: "Flagged", value: flagged || "—", color: "#d9534f", icon: "🚩" },
            // { label: "% Flagged", value: pctFlagged, color: "#2980b9", icon: "📊" },
            { label: "Avg A1c", value: avgA1c, color: "#27ae60", icon: "📈" },
            { label: "Critical (≥10%)", value: critCount, color: "#d9534f", icon: "⚠️" },
            { label: "High (9–10%)", value: highCount, color: "#e67e22", icon: "🔶" },
        ];

        const filterBtns = [
            { key: "all", label: "All Alerts", cls: "btn-primary" },
            { key: "critical", label: "Critical — ≥10%", cls: "btn-danger" },
            { key: "high", label: "High — 9–10%", cls: "btn-warning" },
            { key: "elevated", label: "Elevated — 8–9%", cls: "btn-info" },
        ];

        return (
            <div style={{ background: "#f4f6fa", fontFamily: "'DM Sans', sans-serif" }}>

                {/* ── HEADER ── */}
                <div style={{
                    background: "linear-gradient(120deg, #0f2457 0%, #1e3c72 60%, #2a5298 100%)",
                    padding: "18px 28px", display: "flex", alignItems: "center",
                    justifyContent: "space-between", flexWrap: "wrap", gap: 12,
                    boxShadow: "0 4px 18px rgba(15,36,87,0.18)",
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                        <div style={{ width: 44, height: 44, borderRadius: 12, background: "rgba(255,255,255,0.12)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>🩺</div>
                        <div>
                            <div style={{ color: "#fff", fontWeight: 700, fontSize: 20, letterSpacing: 0.3 }}>A1c Alert Agent</div>
                            <div style={{ color: "rgba(255,255,255,0.55)", fontSize: 12, marginTop: 1 }}>ECM Population Health · Diabetes Management</div>
                        </div>
                    </div>
                    {/* <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        {[
                            { icon: "✦", label: "Run All AI Recs", primary: true },
                            { icon: "↓", label: "Export CSV" },
                            { icon: "↑", label: "New File" },
                        ].map((btn) => (
                            <button key={btn.label} className="btn btn-sm" style={{
                                fontWeight: 600, fontSize: 12, borderRadius: 8, padding: "7px 16px",
                                background: btn.primary ? "#2a5298" : "rgba(255,255,255,0.12)",
                                border: btn.primary ? "1.5px solid #4a7bd4" : "1.5px solid rgba(255,255,255,0.2)",
                                color: "#fff", letterSpacing: 0.3,
                            }}>
                                {btn.icon} {btn.label}
                            </button>
                        ))}
                    </div> */}
                </div>

                <div style={{ padding: "24px 28px" }}>

                    {/* Full-page loading */}
                    {loading && (
                        <div style={{ textAlign: "center", padding: "60px", color: "#7a8599" }}>
                            <div style={{ fontSize: 32, marginBottom: 10 }}>⏳</div>
                            <div style={{ fontSize: 14 }}>Loading dashboard data…</div>
                        </div>
                    )}

                    {error && (
                        <div className="alert alert-danger" style={{ borderRadius: 8 }}>
                            <strong>Error loading data:</strong> {error}
                        </div>
                    )}

                    {!loading && (
                        <>
                            {/* ── SUMMARY CARDS ── */}
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 24 }}>
                                {statCards.map((c) => (
                                    <div key={c.label} style={{ flex: "1 1 110px", minWidth: 100 }}>
                                        <div style={{
                                            background: "#fff", borderRadius: 10, padding: "16px 14px 12px",
                                            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                                            borderTop: `3px solid ${c.color}`, textAlign: "center",
                                        }}>
                                            <div style={{ fontSize: 20 }}>{c.icon}</div>
                                            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 22, fontWeight: 700, color: c.color, lineHeight: 1.1, marginTop: 4 }}>
                                                {c.value}
                                            </div>
                                            <div style={{ fontSize: 11, color: "#7a8599", marginTop: 4, fontWeight: 500 }}>{c.label}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* ── FILTER BAR ── */}
                            <div style={{
                                background: "#fff", borderRadius: 10, padding: "12px 18px", marginBottom: 20,
                                display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                            }}>
                                <span style={{ fontSize: 12, color: "#7a8599", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.8, marginRight: 4 }}>FILTER:</span>
                                {filterBtns.map((btn) => (
                                    <button key={btn.key} onClick={() => this.setFilter(btn.key)}
                                        className={`btn btn-sm ${filter === btn.key ? btn.cls : "btn-default"}`}
                                        style={{ borderRadius: 20, fontWeight: 600, fontSize: 12, padding: "5px 16px", transition: "all 0.15s" }}>
                                        {btn.label}
                                    </button>
                                ))}
                                <select
                                    name="memberStatus"
                                    id="memberStatus"
                                    value={this.state.memberStatus}
                                    onChange={(e) => {
                                        this.setState({ memberStatus: e.target.value, currentPage: 1 }, () => {
                                            this.fetchPage(1);
                                        });
                                    }}
                                    className="form-control input-sm"
                                    style={{ width: "130px", borderRadius: "20px" }}
                                >
                                    <option value="">Status</option>
                                    <option value="Active">Active</option>
                                    <option value="Inactive">Inactive</option>
                                </select>
                                <select
                                    name="careGapType"
                                    id="careGapType"
                                    value={this.state.careGapType}
                                    onChange={(e) => {
                                        this.setState({ careGapType: e.target.value, currentPage: 1 }, () => {
                                            this.fetchPage(1);
                                        });
                                    }}
                                    className="form-control input-sm"
                                    style={{ width: "135px", borderRadius: "20px" }}
                                >
                                    <option value="">Care Gaps</option>
                                    <option value="HIGH_A1C_GAP">High A1C Gap</option>
                                    <option value="MULTI_CHRONIC">Multi Chronic</option>
                                    {/* <option value="SDOH_RISK">SDOH_RISK</option>
                                    <option value="HIGH_ER">HIGH_ER</option> */}
                                </select>
                                <select
                                    name="sort"
                                    id="sort"
                                    value={this.state.sort}
                                    onChange={(e) => {
                                        const [sortBy, sortDir] = e.target.value.split("|");

                                        this.setState(
                                            {
                                                sortBy,
                                                sortDir,
                                                currentPage: 1
                                            },
                                            () => {
                                                this.fetchPage(1);
                                            }
                                        );
                                    }}
                                    className="form-control input-sm"
                                    style={{ width: "200px", borderRadius: "20px" }}
                                >
                                    <option value="">Sort</option>
                                    <option value="MEMBER|ASC">Member (Low → High)</option>
                                    <option value="MEMBER|DESC">Member (High → Low)</option>
                                    <option value="LEAD|ASC">Lead (Low → High)</option>
                                    <option value="LEAD|DESC">Lead (High → Low)</option>
                                </select>

                                <span style={{ marginLeft: "auto", fontSize: 12, color: "#aab3c0", fontStyle: "italic" }}>
                                    {filtered.length} member{filtered.length !== 1 ? "s" : ""} shown
                                </span>
                            </div>

                            {/* ── PATIENT CARDS ── */}
                            <div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: "#1a2340", marginBottom: 14, letterSpacing: 0.4 }}>
                                    PATIENT ALERTS
                                    <span style={{ fontSize: 11, color: "#aab3c0", fontWeight: 400, marginLeft: 8 }}>
                                        Click a card to expand · toggle AI Rec for clinical guidance
                                    </span>
                                </div>

                                {/* Page-level loading overlay */}
                                {pageLoading ? (
                                    <div style={{
                                        background: "#fff", borderRadius: 10, padding: "40px",
                                        textAlign: "center", color: "#7a8599",
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                                    }}>
                                        <div style={{ fontSize: 24, marginBottom: 8 }}>⏳</div>
                                        <div style={{ fontSize: 13 }}>Loading page {currentPage}…</div>
                                    </div>
                                ) : filtered.length === 0 ? (
                                    <div style={{ background: "#fff", borderRadius: 10, padding: "40px", textAlign: "center", color: "#aab3c0", fontSize: 14 }}>
                                        No patients match this filter.
                                    </div>
                                ) : (
                                    filtered.map((p) => <PatientCard key={p.id || p.mrn} p={p} />)
                                )}

                                {/* ── PAGINATION ── */}
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    totalRecords={totalRecords}
                                    pageSize={PAGE_SIZE}
                                    loading={pageLoading}
                                    onPageChange={(page) => this.fetchPage(page)}
                                />
                            </div>
                        </>
                    )}
                </div>

                <style>{`
                    @keyframes fadeSlideIn {
                        from { opacity: 0; transform: translateY(-6px); }
                        to   { opacity: 1; transform: translateY(0); }
                    }
                    .detail-label { font-size: 14px; text-transform: uppercase; letter-spacing: 0.9px; color: #1a2340; font-weight: 600; }
                    .detail-value { font-size: 13px; color: #1a2340; font-weight: 500; margin-top: 3px; }
                    .detail-block { margin-bottom: 4px; }
                    .patient-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.10) !important; }
                    body { background: #f4f6fa !important; }
                `}</style>
            </div>
        );
    }
}

export default A1cDashboard;
